import { LoaderCircle, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  entityCardTypeLabelKeys,
  hasActiveEntityFilters,
  type EntityCardType,
  type EntityCardView,
  type EntityLibraryFilters,
} from '@/features/entities'

export type EntityEditorSaveState = 'error' | 'idle' | 'saved' | 'saving'

type EntityWorkspaceHeaderProps = {
  card: EntityCardView | null
  draftType: EntityCardType | null
  filters: EntityLibraryFilters
  resultCount: number
  saveError: string | null
  saveState: EntityEditorSaveState
  selectedCardHiddenByFilters: boolean
  totalCount: number
}

function getLibrarySummaryText(
  filters: EntityLibraryFilters,
  resultCount: number,
  totalCount: number,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (totalCount === 0) {
    return t('entities.filters.summaryEmpty')
  }

  if (!hasActiveEntityFilters(filters)) {
    return t('entities.filters.summaryVisible', {
      resultCount,
      totalCount,
    })
  }

  return t('entities.filters.summaryFiltered', {
    resultCount,
    totalCount,
  })
}

export function EntityWorkspaceHeader({
  card,
  draftType,
  filters,
  resultCount,
  saveError,
  saveState,
  selectedCardHiddenByFilters,
  totalCount,
}: EntityWorkspaceHeaderProps) {
  const { t } = useTranslation()
  const title = card
    ? card.name
    : draftType
      ? t('entities.workspace.newCardTitle', {
          type: t(entityCardTypeLabelKeys[draftType]),
        })
      : t('entities.workspace.title')
  const description = card
    ? card.secondaryText
    : t('entities.workspace.description')
  const librarySummary = getLibrarySummaryText(filters, resultCount, totalCount, t)
  const showFilterNote =
    hasActiveEntityFilters(filters) || selectedCardHiddenByFilters || totalCount > 0

  return (
    <section className="space-y-4 rounded-[30px] border border-white/70 bg-card/90 p-6 shadow-[0_22px_70px_-34px_rgba(40,31,19,0.44)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t('entities.workspace.title')}
          </p>
          <h2 className="text-3xl text-foreground">{title}</h2>
          <p className="max-w-3xl leading-7 text-muted-foreground">{description}</p>
        </div>
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm text-secondary-foreground">
            {saveState === 'saving' ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t('entities.save.saving')}
              </>
            ) : saveState === 'error' ? (
              <>
                <TriangleAlert className="h-4 w-4" />
                {t('entities.save.error')}
              </>
            ) : saveState === 'saved' ? (
              t('entities.save.saved')
            ) : (
              t('entities.save.ready')
            )}
          </div>
          {card ? (
            <div className="text-right text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {t(entityCardTypeLabelKeys[card.type])}
              {' | '}
              {t('entities.relationships.count', {
                count: card.relationshipCount,
              })}
              {card.hasBrokenLinks
                ? ` | ${t('entities.relationships.repairNeededShort')}`
                : ''}
            </div>
          ) : null}
        </div>
      </div>

      {saveError ? (
        <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {saveError}
        </p>
      ) : null}

      {showFilterNote ? (
        <div className="rounded-[22px] bg-white/70 px-4 py-3 text-sm text-muted-foreground">
          <p>{librarySummary}</p>
          {selectedCardHiddenByFilters ? (
            <p className="mt-2 text-foreground">
              {t('entities.filters.hiddenSelectionNotice')}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
